import { addToCart } from '../../cart';
import {
  FILTER_MOBILE_ICON_SELECTOR,
  FILTER_TREE_STATE_KEY,
  FILTER_TREE_TOGGLE_BUTTON_SELECTOR,
} from '../../constants/filter-tree';
import { handleClickEmbeddedBundle } from '../../dynamicBundle';
import { handleRecentSearchForSuggestionTerm } from '../../instant-search-widget/recent-search';
import { closeQuickAddToCart, handleClickProductItem, quickAddToCart } from '../../product-item';
import { quickView } from '../../product-item/quick-view';
import { pagination } from '../../product-list';
import { handleLimitListOptionClick, handleToggleShowLimitList } from '../../toolbar/limit-list';
import { handleSort } from '../../toolbar/sort';
import { handleViewAsClick } from '../../toolbar/view-as';
import { isMobile, safeParseJSON, scrollToTop } from '../../utils';
import {
  applyFilterOption,
  backFilterTreeMobile,
  clearAllFilter,
  clearFilter,
  closeMobileFilterTree,
  expandCollapseMultiLevel,
  filterOptionCollapse,
  getFilterSettings,
  getFilterTreeParent,
  removeFilterFromRefineBy,
  resetFilterOption,
  showMobileFilterTree,
  toggleFilterTreeButtonDesktop,
  toggleFilterTreeIcon,
} from '../index';
import { optionCollection } from '../option-collection';
import { optionList } from '../option-list';

/**
 * @type {import('../index.d').handleDocumentClick}
 */
export function handleDocumentClick(context, event) {
  const { filterLayout, filterTreeVerticalStyle } = getFilterSettings(context);

  const filterTreeParent = getFilterTreeParent(context);
  const [, setFilterTreeDesktopOpening] = context.useContextState(
    FILTER_TREE_STATE_KEY.DESKTOP_OPENING,
    false
  );
  const [, setCurrentFilterFocus] = context.useContextState(
    FILTER_TREE_STATE_KEY.CURRENT_FOCUS,
    ''
  );

  const _isMobile = isMobile(context?.app?.generalSettings?.breakpointtabletportraitmax);

  const target = event.target;
  const metadataValue = target.dataset.metadata || target.getAttribute('metadata');
  const metadataObject = safeParseJSON(metadataValue);

  const currentButton = target.closest('button');
  if (currentButton && currentButton.id) {
    setCurrentFilterFocus(currentButton.id);
  }

  handleRecentSearchForSuggestionTerm(event);

  const handlers = {
    optionList: context.handler(optionList, 'handleFilterOption'),
    clearAllFilter: context.handler(clearAllFilter, 'clearAllFilter'),
    clearFilter: context.handler(clearFilter, 'clearFilter'),
    removeFilterFromRefineBy: context.handler(removeFilterFromRefineBy, 'removeFilterFromRefineBy'),
    addToCart: context.handler(addToCart, 'addToCart'),
    quickView: context.handler(quickView, 'quickView'),
    optionCollection: context.handler(optionCollection, 'optionCollection'),
    filterOptionCollapse: context.handler(filterOptionCollapse, 'filterOptionCollapse'),
    quickAddToCart: context.handler(quickAddToCart, 'quickAddToCart'),
    applyFilterOption: context.handler(applyFilterOption, 'applyFilterOption'),
    resetFilterOption: context.handler(resetFilterOption, 'resetFilterOption'),
    expandCollapseMultiLevel: context.handler(expandCollapseMultiLevel, 'expandCollapseMultiLevel'),
    goToProductDetail: context.handler(handleClickProductItem, 'handleClickProductItem'),
  };

  if (
    target.closest('.boost-sd__product-item, .boost-sd__product-item-list-view-layout') &&
    !target.closest('.boost-sd__popup-select-option') &&
    (!target.closest('.boost-sd__product-swatch') ||
      target.closest('.boost-sd__product-swatch-more')) &&
    !target.closest('.boost-sd__bulk-quantity') &&
    !metadataObject &&
    !metadataObject?.action
  ) {
    context.handler(handleClickProductItem, 'handleClickProductItem')(context, null, target);
  }

  // action.optionCollection -> collection multi level - prevent default
  // Not prevent when click product banner - !target?.closest('.boost-sd__product-banner-link')
  const isAnchor = target.tagName === 'A';
  const isWidgetInternalLink = !target.closest('.boost-sd__product-banner-link');

  if (
    isAnchor &&
    (!target.getAttribute('href') || metadataObject.action?.optionCollection) &&
    isWidgetInternalLink
  ) {
    event.preventDefault();
  }

  if (metadataObject && metadataObject.action) {
    // event.preventDefault();
    Object.keys(metadataObject.action).forEach((key) => {
      handlers[key](context, metadataObject.action[key], target);
    });
  }

  // click data-action
  const actionId = target.dataset.action || target.getAttribute('data-action');
  if (actionId) {
    const [getActionMapping] = context.useContextState('actionMapping', {});
    const actionMapping = getActionMapping();
    const [key, id] = actionId.split('.');

    if (actionMapping[key] && actionMapping[key][id]) {
      const actionHandler = Object.keys(actionMapping[key][id])[0];
      const action = actionMapping[key][id][actionHandler];
      handlers[actionHandler](context, action, target);
    }
  }

  if (
    target.classList &&
    [...target.classList].some((className) => className.startsWith('boost-sd__sorting-'))
  ) {
    context.handler(handleSort, 'handleSort')(context, target);
  }
  if (
    target.classList.contains('boost-sd__pagination-number') ||
    target.classList.contains('boost-sd__pagination-button')
  ) {
    context.handler(pagination, 'pagination')(context, target);
  }

  if (target.classList.contains('boost-sd__show-limit-list-button')) {
    context.handler(handleToggleShowLimitList, 'handleToggleShowLimitList')(context, target);
  }

  if (target.classList.contains('boost-sd__show-limit-list-option')) {
    context.handler(handleLimitListOptionClick, 'handleLimitListOptionClick')(context, target);
  }

  if (
    target.classList.contains('boost-sd__view-as-icon') ||
    target.closest('.boost-sd__view-as-icon')
  ) {
    context.handler(handleViewAsClick, 'handleViewAsClick')(context, target);
  }

  const filterTreeVerticalStyleOffCanvas =
    filterLayout === 'vertical' && filterTreeVerticalStyle === 'style-off-canvas';
  const styleOffCanvasSelector =
    '#boost-sd__filter-tree-wrapper #boost-sd__filter-tree-vertical--style-off-canvas';
  const styleOffCanvas = context.$(styleOffCanvasSelector, filterTreeParent);

  // click button show result, close , overlay in style off-canvas -> close
  if (
    filterTreeVerticalStyleOffCanvas &&
    !_isMobile &&
    (target.closest('.boost-sd__button.boost-sd__button--result') ||
      target.closest('.boost-sd__filter-tree-vertical-close-filter--left') ||
      target.classList.contains('boost-sd__filter-tree-vertical-sticky-overlay'))
  ) {
    styleOffCanvas?.classList?.add('boost-sd__filter-tree-vertical--hidden');
    setFilterTreeDesktopOpening(false);
    toggleFilterTreeIcon(context);
    document.body.classList.remove('boost-sd__g-no-scroll');
  }

  // Click filter Tree toggle button in desktop
  if (target.closest(FILTER_TREE_TOGGLE_BUTTON_SELECTOR)) {
    // click mobile
    if (target.closest(FILTER_MOBILE_ICON_SELECTOR)) {
      return context.handler(showMobileFilterTree, 'showMobileFilterTree')(context);
    }

    // click desktop
    context.handler(toggleFilterTreeButtonDesktop, 'toggleFilterTreeButtonDesktop')(
      context,
      filterTreeParent
    );
  }

  if (
    target.closest('.boost-sd__filter-tree-vertical-close-filter') ||
    target.closest('.boost-sd__button--close') ||
    target.classList.contains('boost-sd__filter-tree-vertical-sticky-overlay') ||
    target.closest('.boost-sd__filter-tree-vertical-show-result')
  ) {
    _isMobile && context.handler(closeMobileFilterTree, 'closeMobileFilterTree')(context);
  }

  if (target.closest('.boost-sd__button--back')) {
    backFilterTreeMobile(context);
  }

  if (target.closest('.boost-sd__popup-select-option-close-btn')) {
    closeQuickAddToCart(target);
  }

  if (target.closest('.boost-sd__scroll-to-top')) {
    scrollToTop();
  }

  if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
    handleClickEmbeddedBundle(context, target);
  }

  if (target?.closest('.boost-sd__product-item--volume-bundle')) {
    import(
      /* webpackChunkName: 'volume-bundle' */
      '../../volume-bundle'
    ).then((module) => {
      const { handleClickVolumeBundle } = module;
      handleClickVolumeBundle(context, target);
    });
  }
}
